import { ApiFile } from "@/types/common.types.ts";
import { ApiFieldValueTypes } from "@/types/custom-field.types.ts";
import { ApiEmployeeCompensationPerTypes } from "@/types/employees/employee-compensation.types.ts";
import { ApiEmployeeShort } from "@/types/employees/employee.types.ts";

export interface ApiImportData {
  id: number;
  author: ApiEmployeeShort;
  import_type: ApiImportDataType;
  status: ApiImportDataStatus;
  file: ApiFile;
  datetime_created: Date;
  datetime_updated: Date;
  rows_total: number;
  rows_success: number;
  rows_failed: number;
}

export interface ApiImportPreviewOverview {
  import_id: number;
  file_name: string;
  file_uuid: string;
  detected_import_type: ApiImportDataType;
  preview: ApiImportPreviewData;
}

export interface ApiImportParsePossibleTable {
  fields: ApiImportParsePossibleEntity[];
  table_id: number;
  table_name: string;
}

export interface ApiImportParsePossibleEntity {
  field_key: string;
  is_custom: boolean;
  title: string;
  group: string | undefined;
}

export interface ApiImportParse {
  import_id: number;
  file: ApiFile;
  import_type: ApiImportDataType;
  update_behavior: ApiImportFieldUpdateBehavior;
  matching_by: number | null;
  preview: ApiImportPreviewOverview;
  columns: ApiImportParseColumn[];
  possible_tables: ApiImportParsePossibleTable[] | null;
  possible_fields: ApiImportParsePossibleEntity[] | null;
}

export interface ApiImportParseColumn {
  column_index: number;
  header: string;
  as_field: string;
  sample_values: string[];
  import_type: ApiImportDataType;
  count_values_to_create: number;
  new_values: { count: number; sample_values: string[] } | null;
  update_behavior: ApiImportFieldUpdateBehavior;
  invalid_values?: { count: number; sample_values: string[]; is_truncated: boolean };
}

export interface ApiImportPayloadColumn {
  column_index: number;
  import_type: ApiImportDataType;
  as_field: string;
  create_new_values: boolean;
  update_behavior: ApiImportFieldUpdateBehavior;
  table_id?: number | null;
  new_custom_field_name?: string | null;
  new_custom_field_type?: ApiFieldValueTypes | null;
  salary_period?: ApiEmployeeCompensationPerTypes | null;
  fallback_currency?: string | null;
}

export interface ApiImportPayloadCustomTableKey {
  table_id: number;
  field_slugs: string[];
}

export interface ApiImportPayloadSave {
  import_id: number;
  matching_by: number;
  import_behavior: ApiImportGlobalUpdateBehavior;
  columns: ApiImportPayloadColumn[];
  custom_tables_keys: ApiImportPayloadCustomTableKey[];
}

export enum ApiImportGlobalUpdateBehavior {
  update_only = "update_only",
  create_only = "create_only",
  upsert = "upsert",
}

export enum ApiImportFieldUpdateBehavior {
  only_if_empty = "only_if_empty",
  always = "always",
}

export enum ApiImportNewValueBehavior {
  create_new_values = "create_new_values",
  skip_news_records = "skip_new_records",
}

export interface ApiImportPreviewColumn {
  column_index: number;
  header: string;
}

export interface ApiImportPreviewData {
  columns: ApiImportPreviewColumn[];
  rows: string[][];
  rows_count: number;
}

export enum ApiImportDataType {
  employee = "employee",
  compensation = "compensation",
  additional_compensation = "additional_compensation",
  position = "position",
  employment = "employment",
  custom_table = "custom_table",
  time_off_history = "time_off_history",
  time_off_balances = "time_off_balances",
}

export enum ApiImportDataStatus {
  draft = "draft",
  waiting = "waiting",
  in_progress = "in_progress",
  complete = "complete",
  fail = "fail",
}

export interface ApiImportResult {
  import_id: number;
  status: ApiImportDataStatus;
  rows_total: number;
  rows_success: number;
  rows_failed: number;
  errors_user: ApiImportError[];
}

export interface ApiImportError {
  row_number: number;
  row_data: Record<string, string>;
  error: string;
  error_code: ApiImportErrorCode;
  field_name: string;
}

export enum ApiImportErrorCode {
  employee_not_found = "employee_not_found",
  employee_identifier_missing = "employee_identifier_missing",
  employee_already_exists = "employee_already_exists",
  employee_update_skipped = "employee_update_skipped",
  position_missing_effective_date = "position_missing_effective_date",
  employment_missing_effective_date = "employment_missing_effective_date",
  compensation_missing_effective_date = "compensation_missing_effective_date",
  custom_table_missing_effective_date = "custom_table_missing_effective_date",
  invalid_data = "invalid_data",
  email_already_exists = "email_already_exists",
  required_fields_missing = "required_fields_missing",
}

export interface ApiImportParseFieldPayload {
  import_id: number;
  column_index: number;
  import_type: ApiImportDataType;
  as_field: string;
}

export interface ApiImportFieldPreview {
  diff: ApiImportFieldPreviewRow[];
  no_change: number;
  not_found: number;
  total_rows: number;
  values_to_create: string[];
  will_create: number;
  will_skip: number;
  will_update: number;
}

export interface ApiImportFieldPreviewRow {
  action: "update" | "create";
  employee?: ApiEmployeeShort | null;
  employee_identifier?: string | null;
  new_value: string | null;
  old_value: string | null;
  row_number: number;
}
